import { useEffect, useRef, useState } from "react";

/**
 * The chat header's editable title, kept in step with the stored room name.
 *
 * CR-199: the server can rename an AI chat while the page is open (auto-naming
 * after the first reply), so a stored-name change must never clobber what the
 * user is typing. The sync from the stored name is skipped while the title
 * field has focus or holds a typed edit the debounce has not settled yet, and
 * the title resyncs on blur when there is no pending edit. A user rename wins
 * either way: the server's compare-and-set only replaces a default name, so a
 * rename stored earlier is kept and one typed later overwrites it.
 *
 * TitleInput has its own focus guard on its internal value; this one guards
 * the controlled `title` too, which the not-editing overlay of
 * TitleInputWithLink renders and the caller's debounce compares against.
 * Both are needed.
 *
 * CR-97: "typed" answers "did the user type since the title last settled?"
 * (see nextMessageRoomName). An edit reverted to the settled value before the
 * debounce fires is not a pending edit — the debounce never fires for it.
 */
export function useChatTitleSync(storedName: string | null | undefined, roomId: string) {
  const [title, setTitle] = useState(storedName ?? "");
  const typedRef = useRef(false);
  const focusedRef = useRef(false);
  // The value the field last settled on (a stored-name sync or a debounce).
  const settledRef = useRef(storedName ?? "");

  // A room switch can unmount the focused input without a blur event, and a
  // keystroke made in the previous room must never be consumed by the next
  // room's mount-time debounce.
  useEffect(() => {
    typedRef.current = false;
    focusedRef.current = false;
  }, [roomId]);

  useEffect(() => {
    if (focusedRef.current || typedRef.current) {
      return;
    }
    settledRef.current = storedName ?? "";
    setTitle(storedName ?? "");
  }, [storedName]);

  const onTitleChange = (next: string) => {
    typedRef.current = next !== settledRef.current;
    setTitle(next);
  };

  /**
   * Read-and-clear, for the caller's debounce handler: whether the user typed
   * since the title last settled. The debounced value becomes the settled one.
   */
  const consumeTypedEdit = (debouncedValue: string): boolean => {
    const typed = typedRef.current;
    typedRef.current = false;
    settledRef.current = debouncedValue;
    return typed;
  };

  const onTitleFocus = () => {
    focusedRef.current = true;
  };

  const onTitleBlur = () => {
    focusedRef.current = false;
    if (!typedRef.current) {
      settledRef.current = storedName ?? "";
      setTitle(storedName ?? "");
    }
  };

  return { title, setTitle, consumeTypedEdit, onTitleChange, onTitleFocus, onTitleBlur };
}
